import { UserInfo } from '../../../types/UserInfoTypes';
import { ConfigurationItem, isFieldConfigurationItem } from '../../parser/ParserTypes';
import {
  AutomationStructureWithParentType,
  RecordTypesLabelsAndStepsByNameAndObjectName,
} from '../types';
import { createRecordTypeIdentifier } from '../pills/pillsUtils';
import { RECORD_TYPE } from '@server/parser';

export const getCardListFromConfigurationItem = (
  configurationItems: ConfigurationItem[],
  parentObjectName?: string,
) => {
  return configurationItems.map((configurationItem) => {
    const relatedObjects =
      configurationItem.relatedObjects?.filter((obj) => obj !== parentObjectName) ?? [];

    return {
      id: configurationItem.id,
      dependencyType: isFieldConfigurationItem(configurationItem)
        ? configurationItem.fieldType
        : '',
      parentType: configurationItem.parentType,
      name: configurationItem.name,
      label: configurationItem.label,
      objectName: configurationItem.objectApiNames?.[0] ?? parentObjectName ?? '',
      relatedObjects,
      changedAt: configurationItem.updatedAt,
      changedBy: configurationItem.updatedBy,
      isActive: !!configurationItem.isActive,
      metadataId: configurationItem.metadataId,
    };
  });
};

export const getCardListFromParsedRecordTypes = (
  parsedRecordTypes: ConfigurationItem[],
  parentObjectName: string,
) => {
  return parsedRecordTypes.map((rt) => ({
    id: rt.id,
    parentType: RECORD_TYPE,
    dependencyType: RECORD_TYPE,
    description: rt.description,
    name: rt.name,
    label: rt.label,
    link: rt.link,
    objectName: parentObjectName,
    changedAt: rt.updatedAt,
    changedBy: rt.updatedBy,
    isActive: !!rt.isActive,
    relatedObjects: [],
    metadataId: rt.metadataId,
  }));
};

export const getCardListFromAutomationStructure = (
  automations: AutomationStructureWithParentType[],
  users?: UserInfo[],
) => {
  return automations.map((automation) => ({
    id: automation.automationId,
    dependencyType: automation.type,
    parentType: automation.parentType,
    name: automation.name,
    label: automation.name,
    objectName: automation.objectName,
    changedAt: automation.updatedAt,
    changedBy: users?.find((user) => user.id === automation.updatedById)?.name,
    isActive: true,
    metadataId: automation.automationId,
  }));
};

export const getRecordTypeNamesAndSteps = ({
  funnelsData = {},
  crmOrgId,
}: {
  funnelsData?: FunnelsData;
  crmOrgId: string;
}) => {
  const recordTypesByObjectName: RecordTypesLabelsAndStepsByNameAndObjectName = {};

  Object.values(funnelsData).forEach((funnel) => {
    if (funnel?.crmOrgId !== crmOrgId) return; //don't show record types from other orgs
    const { name, label } = funnel?.recordType ?? {};
    const objectName = funnel?.recordType?.objectName;
    const _label = label ?? name;
    const recordTypeName = createRecordTypeIdentifier({
      recordTypeName: name,
      objectName,
      funnelId: funnel.id,
    });
    const leadingFieldName = funnel.funnelDetails.leadingObject.fieldName;
    const steps = funnel.funnelDetails.stages.map((stage) => {
      return {
        value: stage._stageId,
        label: stage.stageName,
      };
    });

    if (objectName) {
      recordTypesByObjectName[objectName] = {
        ...recordTypesByObjectName[objectName],
        [recordTypeName]: { label: _label, leadingFieldName, steps: steps },
      };
    }
  });

  return recordTypesByObjectName;
};

export const translateSortKeyToConfigurationProp = (sortKey: string) => {
  switch (sortKey) {
    case 'status':
      return 'isActive';
    default:
      return sortKey;
  }
};
